import React, { ReactNode, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import cls from "./styles.module.scss";

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
}

/**
 * Нижняя шторка из прототипа: одна на все списки, контент подставляется.
 * Держим в DOM ещё `--dur-3` после закрытия, чтобы доиграть анимацию.
 */
export const Sheet: React.FC<SheetProps> = ({ isOpen, onClose, title, children }) => {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      return undefined;
    }
    const id = window.setTimeout(() => setMounted(false), 340);
    return () => window.clearTimeout(id);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!mounted) return null;

  const host = document.getElementById("modal-root") ?? document.body;

  return ReactDOM.createPortal(
    <div className={classNames(cls.host, isOpen && cls.open)}>
      <button
        type="button"
        aria-label="Закрыть"
        className={cls.scrim}
        onClick={onClose}
      />
      <div className={cls.sheet} role="dialog" aria-modal="true">
        <div className={cls.grab} />
        <div className={classNames("t-h2", cls.title)}>{title}</div>
        <div className={cls.scroll}>
          <div className={cls.body}>{children}</div>
        </div>
        <button type="button" className={cls.close} onClick={onClose}>
          {t("sheet.close")}
        </button>
      </div>
    </div>,
    host
  );
};
